/**
 * Centralized keyboard state and pointer-lock-friendly key handling.
 */
export class InputManager {
  constructor() {
    /** @type {Set<string>} */
    this.keysDown = new Set();
    /** @type {Set<string>} */
    this._keysJustDown = new Set();
    /** @type {((event: KeyboardEvent) => void) | null} */
    this._onKeyDown = null;
    /** @type {((event: KeyboardEvent) => void) | null} */
    this._onKeyUp = null;
    /** @type {Window | Document | null} */
    this._target = null;
  }

  /** @param {Window | Document} target */
  attach(target) {
    if (this._target) this.detach();

    this._target = target;
    this._onKeyDown = (e) => {
      if (this._shouldBlockBrowserShortcuts(e)) {
        e.preventDefault();
      }
      if (!this.keysDown.has(e.code)) {
        this._keysJustDown.add(e.code);
      }
      this.keysDown.add(e.code);
    };
    this._onKeyUp = (e) => {
      this.keysDown.delete(e.code);
    };

    target.addEventListener('keydown', this._onKeyDown);
    target.addEventListener('keyup', this._onKeyUp);
  }

  detach() {
    if (!this._target || !this._onKeyDown || !this._onKeyUp) return;
    this._target.removeEventListener('keydown', this._onKeyDown);
    this._target.removeEventListener('keyup', this._onKeyUp);
    this._target = null;
    this._onKeyDown = null;
    this._onKeyUp = null;
    this.keysDown.clear();
    this._keysJustDown.clear();
  }

  update() {
    // Reserved for action maps / gamepad
  }

  isKeyDown(code) {
    return this.keysDown.has(code);
  }

  /** One-shot per press (e.g. jump). */
  consumeKey(code) {
    if (!this._keysJustDown.has(code)) return false;
    this._keysJustDown.delete(code);
    return true;
  }

  _shouldBlockBrowserShortcuts(event) {
    const blocked = new Set([
      'Space',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
    ]);
    return blocked.has(event.code);
  }
}
